import React, { forwardRef, memo, useEffect, useState } from "react";

import { ETableSortDefaultValue, ITableRef, ITableProps } from "./types";
import { useTable } from "./useTable";
import { ObjectUtils, ClassNames } from "../utils";
import { moduleConfig } from "../module.config";
import { Message } from "../message";
import { TableFilter } from "./TableFilter";
import { TableSearch } from "./TableSearch";
import { TablePagination } from "./TablePagination";
import TableSearchBox from "./TableSearchBox";

export const Table = memo(
  forwardRef<ITableRef, ITableProps>((props, ref) => {
    const {
      tableSearchBox = true,
      filters,
      onFilters,
      itemPerPages: itemsPerPage = 10,
    } = props;
    const {
      state: beforeState,
      fetchData,
      setIsSearchBoxActive,
      isSearchBoxActive,
      sortObj,
    } = useTable(props, ref);
    const [isHeaderActive] = useState(true);
    const [isFilterLoading, setIsFilterLoading] = useState(false);

    useEffect(() => {
      (async () => {
        setIsFilterLoading(true);
        await fetchData({ pagination: { itemsPerPage, pageNumber: 1 } });
        setIsFilterLoading(false);
      })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters, itemsPerPage]);
    
    const state = onFilters ? onFilters(beforeState, filters) : beforeState;

    return (
      <div
        className={`${ClassNames({
          Table: true,
          [props.className as string]: props.className,
        })} table-responsive-md`}
      >
        {props.searchBox ? (
          <TableSearch
            {...props.searchBox}
            structure={props.structure}
            onActive={() => setIsSearchBoxActive(true)}
            onOffSearch={() => setIsSearchBoxActive(false)}
          />
        ) : null}

        {!isSearchBoxActive && props.filter && props.filter.structure.length ? (
          <TableFilter
            {...props.filter}
            onSubmit={(params) => fetchData({ params })}
          />
        ) : null}

        {!isSearchBoxActive ? (
          <>
            <table className={`table table-hover ${props.className}`}>
              {isHeaderActive && (
                <>
                  <thead className="Table__Head">
                    <tr>
                      {props.structure.map((item, key) => {
                        const enableSort = !!item.sort;
                        const sortKey =
                          ObjectUtils.getIn(item, "key") ||
                          ObjectUtils.getIn(item, "sort.key");
                        const sortValue = state.params[sortKey];

                        const increaseValue = ObjectUtils.getIn(
                          item,
                          "sort.increaseValue",
                          ETableSortDefaultValue.INCREASE
                        );
                        const isIncrease = sortValue === increaseValue;

                        const descreaseValue = ObjectUtils.getIn(
                          item,
                          "sort.descreaseValue",
                          ETableSortDefaultValue.DESCREASE
                        );
                        const isDescrease = sortValue === descreaseValue;

                        return (
                          <th
                            key={key}
                            style={item.style}
                            className={ClassNames({
                              [item.className as string]: !!item.className,
                              enableSort,
                              increase: isIncrease,
                              descrease: isDescrease,
                            })}
                            onClick={() => {
                              if (enableSort) {
                                if (isDescrease)
                                  return fetchData({
                                    params: {
                                      ...sortObj,
                                      [sortKey]: increaseValue,
                                    },
                                  });
                                return fetchData({
                                  params: {
                                    ...sortObj,
                                    [sortKey]: descreaseValue,
                                  },
                                });
                              }
                            }}
                          >
                            {item.name}
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                </>
              )}

              <tbody className="Table__Body">
                {/* Loaded */}
                {tableSearchBox && state.count !== -1 && (
                  <TableSearchBox
                    structure={props.structure}
                    onSubmit={fetchData}
                  />
                )}

                {(() => {
                  if (!state.data || isFilterLoading) return null;
                  const endIndex =
                    state.pagination.pageNumber * state.pagination.itemsPerPage;

                  const slicedData = state.data.slice(
                    (state.pagination.pageNumber - 1) *
                      state.pagination.itemsPerPage,
                    endIndex < state.data.length ? endIndex : state.data.length
                  );
                  return slicedData.map((item: any, itemKey: any) => {
                    item["_order"] =
                      (state.pagination.pageNumber - 1) *
                        state.pagination.itemsPerPage +
                      itemKey +
                      1;

                    return (
                      <tr
                        key={itemKey}
                        className={item["_className"]}
                        onClick={() => {
                          props.onRowClick && props.onRowClick(item);
                        }}
                      >
                        {props.structure.map((column, columnKey) => {
                          return (
                            <td
                              className={column.className || ""}
                              style={column.style}
                              key={columnKey}
                            >
                              {(() => {
                                if (column.render)
                                  return column.render(item, fetchData, column);
                                if (column.key)
                                  return `${ObjectUtils.getIn(
                                    item,
                                    column.key,
                                    ""
                                  )}`;
                                return "--";
                              })()}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  });
                })()}
              </tbody>
            </table>

            {(() => {
              if (state.count === -1 || isFilterLoading)
                return (
                  <Message
                    type="loading"
                    message={moduleConfig.translate("fetching-data")}
                  />
                );
              if (state.error)
                return <Message type="error" message={state.error} />;
              if (!state.count || !state.data.length)
                return (
                  <Message
                    type="emptyData"
                    message={moduleConfig.translate("no-data")}
                  />
                );
            })()}

            {state.count && state.count !== -1 ? (
              <TablePagination fetchData={fetchData} state={state} />
            ) : null}
          </>
        ) : null}
      </div>
    );
  }),
  (prevProps, nextProps) => {
    if (!Object.is(prevProps.filters, nextProps.filters)) return false;
    if (!prevProps.enableReinitialize) return true;
    return false;
  }
);
